import React from 'react';
import { Alert, Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useGhgOrganization } from 'src/common/hooks';
import {
  useCommitmentReadiness,
  useCommitmentTarget,
  useRabenConsent,
} from 'src/Ghg/hooks';
import TargetReadinessChecklist from './TargetReadinessChecklist';
import TargetSection from './TargetSection';

// RG 28.08 2.3 (K7): the target part of the tab. It is shown for every Letter
// state, so it must never ask for anything that answers 409 before the Letter
// is accepted: the readiness endpoint works at any time, the target GET only
// runs once `consent === true`.
//
//   - always:                  the heading
//   - target not set yet:      info note + readiness checklist; below it the
//                              TargetSection once the Letter is accepted and
//                              there is a year to pick or a refusal to show.
//                              The note says what is still missing, so it is
//                              left out once the Letter is accepted and a
//                              year is eligible: the form below is then the
//                              next step (the checklist stays)
//   - target set + accepted:   the TargetSection alone (checklist is gone)
//
// It never assumes the shape of the readiness answer: without a `years` array
// only the heading is rendered.
export default function TargetArea() {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'ghg.supplierCommitment.target',
  });
  const organization = useGhgOrganization();
  const { data: consentData } = useRabenConsent(organization.id);
  const consentAccepted = consentData?.consent === true;
  const { data: readiness } = useCommitmentReadiness(organization.id);
  const { data: targetState } = useCommitmentTarget(organization.id, {
    enabled: consentAccepted,
  });

  const heading = (
    <Typography variant="h5" sx={{ mb: 2 }}>
      {t('title')}
    </Typography>
  );

  if (!consentData || !Array.isArray(readiness?.years)) {
    return <Box sx={{ mt: 4 }}>{heading}</Box>;
  }

  const eligibleYears = readiness?.eligible_years ?? [];
  const targetSet = readiness?.target_set === true;

  if (targetSet) {
    return (
      <Box sx={{ mt: 4 }}>
        {heading}
        {consentAccepted && <TargetSection />}
      </Box>
    );
  }

  const showSection =
    consentAccepted && (eligibleYears.length > 0 || !!targetState?.declined);
  const showMissingNote = !(consentAccepted && eligibleYears.length > 0);

  return (
    <Box sx={{ mt: 4 }}>
      {heading}
      {/* Not `color="text.secondary"`: in the GHG (Raben) theme that is red.
          Raben 14.10: the note ends with a pointer to the Guide, which is
          Appendix 1 of the Letter PDF (the file above in this tab). */}
      {showMissingNote && (
        <Alert severity="info" role="status" sx={{ mb: 2 }}>
          <span>{t('noEligibleYears')}</span> <span>{t('moreInfoGuide')}</span>
        </Alert>
      )}
      <TargetReadinessChecklist
        consent={consentData.consent}
        acceptedAt={
          consentData.accepted_at ?? consentData.snapshot?.accepted_on
        }
        canDecide={consentData.is_direct_member === true}
        years={readiness?.years ?? []}
        eligibleYears={eligibleYears}
      />
      {showSection && <TargetSection />}
    </Box>
  );
}
